import React, { useState, useRef, useEffect } from 'react';
import { WithClassName, WithStyle } from '@/types/component';

type Direction = 'x' | 'y';
type HandlePosition = 'start' | 'end';
export type ElementSize = {
  width: number;
  height: number;
};

function convertDimensionToStyle(val: 'auto' | 'full' | number): string {
  if (val === 'full') return '100%';
  return 'auto';
}

type Props = WithClassName & {
  children: React.ReactNode;
  direction: Direction;
  handlePosition?: HandlePosition;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  initialWidth?: number | 'auto' | 'full';
  initialHeight?: number | 'auto' | 'full';
  handleColor?: string;
  handleHoverColor?: string;
  disableResize?: boolean;
  size?: ElementSize;
  onSizeChange?: (size: ElementSize) => void;
};

export function ResizableContainer({
  children,
  direction,
  handlePosition = 'end',
  minWidth = 100,
  minHeight = 100,
  maxWidth = Infinity,
  maxHeight = Infinity,
  initialWidth = 'auto',
  initialHeight = 'auto',
  handleColor = 'transparent',
  handleHoverColor = '#081134',
  className,
  disableResize,
  // EXPERIMENTAL: for "Controlled" mode
  size,
  onSizeChange,
}: Props) {
  const [internalSize, setInternalSize] = useState<ElementSize>({ width: 0, height: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const resizableRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef({ x: 0, y: 0 });

  // ==============================================
  // FUNCTIONS
  // ==============================================
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const getHandleStyles = (): WithStyle['style'] => {
    const baseStyle: WithStyle['style'] = {
      position: 'absolute',
      zIndex: 100,
      borderRadius: '4px',
      backgroundColor: isHovering ? handleHoverColor : handleColor,
      transition: 'background-color 0.2s',
    };

    if (direction === 'x') {
      return {
        ...baseStyle,
        top: 0,
        bottom: 0,
        [handlePosition === 'end' ? 'right' : 'left']: -6,
        width: 12,
        height: '100%',
        cursor: 'ew-resize',
      };
    } else {
      return {
        ...baseStyle,
        left: 0,
        right: 0,
        [handlePosition === 'end' ? 'bottom' : 'top']: -6,
        width: '100%',
        height: 12,
        cursor: 'ns-resize',
      };
    }
  };

  // ==============================================
  // EFFECTS
  // ==============================================
  useEffect(() => {
    if (resizableRef.current) {
      const rect = resizableRef.current.getBoundingClientRect();

      const width =
        initialWidth === 'auto' || initialWidth === 'full'
          ? rect.width
          : (size?.width === 0 ? initialWidth : size?.width) || initialWidth;
      const height =
        initialHeight === 'auto' || initialHeight === 'full'
          ? rect.height
          : (size?.height === 0 ? initialHeight : size?.height) || initialHeight;

      setInternalSize({
        width,
        height,
      });

      onSizeChange?.({
        width,
        height,
      });
    }
  }, [initialWidth, initialHeight, onSizeChange, size?.height, size?.width]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;

      setInternalSize((prevSize) => {
        let newWidth = prevSize.width;
        let newHeight = prevSize.height;

        if (direction === 'x') {
          const widthDelta = handlePosition === 'end' ? deltaX : -deltaX;
          const viewportWidth = window.innerWidth;
          const elementRect = resizableRef.current?.getBoundingClientRect();
          const elementLeft = elementRect?.left || 0;

          const maxAllowedWidth = handlePosition === 'end' ? viewportWidth - elementLeft : elementRect?.right || 0;

          const effectiveMaxWidth = Math.min(maxWidth, maxAllowedWidth);

          newWidth = Math.min(Math.max(prevSize.width + widthDelta, minWidth), effectiveMaxWidth);
        } else {
          const heightDelta = handlePosition === 'end' ? deltaY : -deltaY;
          const viewportHeight = window.innerHeight;
          const elementRect = resizableRef.current?.getBoundingClientRect();
          const elementTop = elementRect?.top || 0;

          const maxAllowedHeight = handlePosition === 'end' ? viewportHeight - elementTop : elementRect?.bottom || 0;

          const effectiveMaxHeight = Math.min(maxHeight, maxAllowedHeight);

          newHeight = Math.min(Math.max(prevSize.height + heightDelta, minHeight), effectiveMaxHeight);
        }

        const result: ElementSize = { width: newWidth, height: newHeight };

        onSizeChange?.(result);
        return result;
      });

      startPosRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, direction, handlePosition, minWidth, maxWidth, minHeight, maxHeight, onSizeChange]);

  // ==============================================
  // JSX
  // ==============================================

  if (disableResize)
    return (
      <div
        className="h-full grow"
        style={{
          width: convertDimensionToStyle(initialWidth),
          height: convertDimensionToStyle(initialHeight),
        }}
      >
        {children}
      </div>
    );

  return (
    <div
      ref={resizableRef}
      style={{
        width: direction === 'x' ? (size?.width ?? internalSize.width) : convertDimensionToStyle(initialWidth),
        height: direction === 'y' ? (size?.height ?? internalSize.height) : convertDimensionToStyle(initialHeight),
      }}
    >
      <div className={className}>{children}</div>
      <div
        tabIndex={0}
        role="button"
        style={getHandleStyles()}
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`absolute inset-0 flex items-center justify-center ${isHovering ? 'opacity-100' : 'opacity-0'} transition-opacity`}
        >
          <div className="mx-0.5 h-1 w-1 rounded-full bg-white" />
          <div className="mx-0.5 h-1 w-1 rounded-full bg-white" />
          <div className="mx-0.5 h-1 w-1 rounded-full bg-white" />
        </div>
      </div>
    </div>
  );
}
